import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { select, Store } from '@ngrx/store';

import { debounceTime, filter, map, take, takeLast, takeUntil, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { currentUser, User } from '@core/auth';
import { AppState } from '@core/reducers';
import { LocalBrowserStorage } from '@core/_services/local-browser.storage';
import { oneMinute } from '@core/_utils/date';

import { environment } from '../../../environments/environment';
import { AdminService } from '@views/pages/admin/admin.service';
import { Page } from '@views/pages/admin/models/page.model';

interface Activity {
  countClicks: number;
  countClicksDashboard: number;
  updatedAt: Date;
}

const SAVE_ACTIVITY = `${environment.apiPrefix}/clicks-monitoring/save-activities`;

const EMPTY_ACTIVITY: Activity = {
  countClicks: 0,
  countClicksDashboard: 0,
  updatedAt: new Date(),
};

const isNavigationEnd = (e) => e instanceof NavigationEnd;

const fullname = (firstName, lastName) => `${firstName} ${lastName}`;
const getEmptyActivity = () => ({ ...EMPTY_ACTIVITY });

const STORAGE_NAME = 'clicks-metrics';

@Injectable({
  providedIn: 'root',
})
export class ClicksMetricService implements OnDestroy {
  private static ActivityProcess(target: any, key: string, descriptor: PropertyDescriptor) {
    let originalMethod = descriptor.value;

    descriptor.value = function (this: ClicksMetricService) {
      originalMethod.apply(this);
      this.updateCurrentActivity();
      this.storage.setItem(STORAGE_NAME, this.activities);
      this.userMadeAnActivity();
    };
  }

  private activities = [];
  private fullname: string = null;
  private pageTitles = null;
  private unsubscribe$ = new Subject();
  private userActivity$ = new Subject();
  private currentActivity: Activity = getEmptyActivity();
  private lastIndexFromPrevVisit = null;

  constructor(
    private router: Router,
    private http: HttpClient,
    private store: Store<AppState>,
    private storage: LocalBrowserStorage,
    private adminService: AdminService,
  ) {
    this.createActivityClicks = this.createActivityClicks.bind(this);
    this.setUserFullname = this.setUserFullname.bind(this);
    this.saveActivities = this.saveActivities.bind(this);

    this.observeForUserActivity();
    this.pullOutUserFullnameFromStore();

    this.setActivities();
    this.lastIndexFromPrevVisit = this.activities.length - 1;

    const route$ = this.router.events.pipe(filter(isNavigationEnd), takeUntil(this.unsubscribe$));

    route$.subscribe((e: NavigationEnd) => {
      this.getPage(this.getSlug(e.url)).subscribe((page) => {
        if (!page) return;

        this.pageTitles = this.getTitles(page);
        this.createActivityClicks();
      });
    });
  }

  @ClicksMetricService.ActivityProcess
  public incrementPageClicks() {
    this.currentActivity.countClicks++;
  }

  @ClicksMetricService.ActivityProcess
  public incrementDashboardClicks() {
    this.currentActivity.countClicksDashboard++;
  }

  public async saveActivities() {
    this.activities = this.storage.getItem(STORAGE_NAME);

    this.storage.setItem(STORAGE_NAME, []);

    await this.http
      .post(SAVE_ACTIVITY, { data: this.activities })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.activities = [];
        this.createNewUserActivity();
      });
  }

  private createActivityClicks() {
    if (!this.pageTitles) return;
    if (this.activityIsEmpty()) return;

    this.activities.push({ ...getEmptyActivity(), ...this.pageTitles, userName: this.fullname });
    this.storage.setItem(STORAGE_NAME, this.activities);
    this.createNewUserActivity();
  }

  private pullOutUserFullnameFromStore() {
    this.store.pipe(select(currentUser), takeUntil(this.unsubscribe$)).subscribe(this.setUserFullname);
  }

  private setActivities() {
    if (!this.storage.getItem(STORAGE_NAME)) {
      this.storage.setItem(STORAGE_NAME, []);
      this.activities = this.storage.getItem(STORAGE_NAME);
      return;
    }

    this.activities = this.storage.getItem(STORAGE_NAME);
  }

  private setUserFullname = (user: User) => (this.fullname = user ? fullname(user.firstName, user.lastName) : null);

  private createNewUserActivity = () => (this.currentActivity = getEmptyActivity());

  private userMadeAnActivity = () => this.userActivity$.next();

  private getSlug = (url) => url.replace('/', '');

  private observeForUserActivity = () =>
    this.userActivity$.pipe(debounceTime(oneMinute() / 4), takeUntil(this.unsubscribe$)).subscribe(this.saveActivities);

  private getTitles = (page: Page) => ({ titleRu: page.titleRu, titleEn: page.titleEn });

  private activityIsEmpty = () =>
    this.currentActivity.countClicksDashboard === 0 && this.currentActivity.countClicks === 0;

  private updateCurrentActivity = () => {
    if (!this.pageTitles) return;
    if (this.activityIsEmpty()) return;

    const lastIndex = this.activities.length - 1;

    const activity = { ...this.currentActivity, ...this.pageTitles, userName: this.fullname, updatedAt: new Date() };

    if (this.lastIndexFromPrevVisit === lastIndex) {
      this.activities.push(activity);
      return;
    }

    this.activities[this.activities.length === 0 ? 0 : lastIndex] = activity;
  };

  private getPage(slug) {
    return this.adminService.getPageBySlug(slug).pipe(takeUntil(this.unsubscribe$));
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
